import { Dimensions, EdgeType, Profile, RingSizeStandard } from '@ringo/types'
import { getNumberRange } from './jsHelper'
import { getNearestNumber } from './numberHelper'
import { RingDiameterConversionValues } from './ring-diameter-conversions'

export function findNearestDiameter(
    diameter: number,
    diametersList: Array<{
        DIAMETER: number
        CUSTOM_WIDTH: number
        USA_CANADA: string
        ENGLAND: string | '--'
        SWITZERLAND: number | string
    }>,
    targetStandard: RingSizeStandard
): {
    DIAMETER: number
    CUSTOM_WIDTH: number
    USA_CANADA: string
    ENGLAND: string | '--'
    SWITZERLAND: number | string
} {
    const sortedByDifference = diametersList.sort(
        (a: { DIAMETER: number }, b: { DIAMETER: number }) => Math.abs(diameter - a.DIAMETER) - Math.abs(diameter - b.DIAMETER)
    )

    return sortedByDifference.filter((conversionMap) => conversionMap[targetStandard] !== '--')[0]
}

export function getCircumferenceFromDiameter(diameter: number, decimalPlaces = 1) {
    return parseFloat((2 * Math.PI * (diameter / 2)).toFixed(decimalPlaces))
}

export function getDiameterFromCircumference(circumference: number | null) {
    if (!circumference) {
        return 0
    }

    return parseFloat((circumference / Math.PI).toFixed(2))
}

export function getStandardSpecificValueFromDiameter(
    standard: RingSizeStandard,
    diameter: number,
    profile: Profile
): { newDiameter: number; standardValue: number | string } {
    if (RingSizeStandard.CUSTOM_WIDTH === standard && profile === Profile.SinterringAxial) {
        const nearestWidth = getNearestNumber(getNumberRange(47, 69, 2), getCircumferenceFromDiameter(diameter))
        return { standardValue: nearestWidth, newDiameter: getDiameterFromCircumference(nearestWidth) }
    } else if (RingSizeStandard.CUSTOM_WIDTH === standard) {
        return { standardValue: getCircumferenceFromDiameter(diameter), newDiameter: diameter }
    } else if (RingSizeStandard.DIAMETER === standard) {
        return { standardValue: diameter, newDiameter: diameter }
    } else {
        const nearestConversion = findNearestDiameter(diameter, RingDiameterConversionValues, standard)
        return { standardValue: nearestConversion[standard], newDiameter: nearestConversion['DIAMETER'] }
    }
}

export function getDiameterFromStandardSpecificValue(standard: RingSizeStandard, standardValue: number | string): number {
    switch (standard) {
        case RingSizeStandard.DIAMETER:
            return standardValue as number
        case RingSizeStandard.ENGLAND:
        case RingSizeStandard.SWITZERLAND:
        case RingSizeStandard.USA_CANADA:
            return RingDiameterConversionValues.find((item) => item[standard] === standardValue)?.DIAMETER as number
        case RingSizeStandard.CUSTOM_WIDTH:
            return getDiameterFromCircumference(standardValue as number)
    }
}

export function getMoldingFromRadius(radius: number, ringHeight: number, ringWidth: number) {
    const molding = ((Math.sqrt(radius * radius - 0.5 * ringWidth * 0.5 * ringWidth) - radius) * -100) / ringHeight
    return parseFloat(molding.toFixed(1))
}

export function hasEdges(
    dimensions: Pick<Dimensions, 'edgeTypeInner' | 'edgeTypeOuter' | 'edgeInner' | 'edgeOuter'>,
    expectedEdgeInnerType: EdgeType,
    expectedEdgeOuterType: EdgeType
) {
    return hasInnerEdge(dimensions, expectedEdgeInnerType) && hasOuterEdge(dimensions, expectedEdgeOuterType)
}

export function hasInnerEdge(dimensions: Pick<Dimensions, 'edgeTypeInner' | 'edgeInner'>, expectedEdgeType: EdgeType) {
    if (expectedEdgeType === EdgeType.BEVEL || expectedEdgeType === EdgeType.ROUND) {
        return !!(dimensions.edgeTypeInner && dimensions.edgeTypeInner === expectedEdgeType && dimensions.edgeInner)
    }

    return dimensions.edgeTypeInner === EdgeType.NO_EDGE || !dimensions.edgeInner
}

export function hasOuterEdge(dimensions: Pick<Dimensions, 'edgeTypeOuter' | 'edgeOuter'>, expectedEdgeType: EdgeType) {
    if (expectedEdgeType === EdgeType.BEVEL || expectedEdgeType === EdgeType.ROUND) {
        return !!(dimensions.edgeTypeOuter && dimensions.edgeTypeOuter === expectedEdgeType && dimensions.edgeOuter)
    }

    return dimensions.edgeTypeOuter === EdgeType.NO_EDGE || !dimensions.edgeOuter
}

export const numberOfDecimals = {
    heigth: { default: 2, sinterringAxial: 0 },
    width: { default: 2, tube: 0 },
    groove: { default: 2 },
    diameter: { default: 2, customWidth: 1 },
    moldOuter: 2,
    moldInner: 2,
    decreaseWall: 2,
    radius: 2,
    sideHeight: 2,
    edge: 2,
    slantAngle: 2,
    sintering: { sliceWidth: 2 },
}
